/**
 * JSON Schema validation utilities
 * Validates data against JSON Schema with visual feedback
 */

export interface ValidationError {
  path: string
  message: string
  keyword: string
  params?: Record<string, unknown>
}

export interface ValidationResult {
  valid: boolean
  errors: ValidationError[]
}

/**
 * Simple JSON Schema validator
 * Supports a subset of JSON Schema Draft-07
 */
export function validateJsonSchema(
  data: unknown,
  schema: Record<string, unknown>,
  path: string = '$'
): ValidationResult {
  const errors: ValidationError[] = []

  function validate(value: unknown, schemaNode: Record<string, unknown>, currentPath: string): void {
    // Type validation
    if (schemaNode.type) {
      const types = Array.isArray(schemaNode.type) ? schemaNode.type : [schemaNode.type]
      const actualType = getJsonType(value)

      if (!types.includes(actualType) && !(actualType === 'integer' && types.includes('number'))) {
        // Check for nullable
        if (!(value === null && (types.includes('null') || schemaNode.nullable))) {
          errors.push({
            path: currentPath,
            message: `Expected ${types.join(' | ')}, got ${actualType}`,
            keyword: 'type',
            params: { expected: types, actual: actualType }
          })
          return
        }
      }
    }

    // Enum validation
    if (schemaNode.enum && Array.isArray(schemaNode.enum)) {
      if (!schemaNode.enum.includes(value)) {
        errors.push({
          path: currentPath,
          message: `Value must be one of: ${schemaNode.enum.join(', ')}`,
          keyword: 'enum',
          params: { allowed: schemaNode.enum, actual: value }
        })
      }
    }

    // Const validation
    if ('const' in schemaNode) {
      if (value !== schemaNode.const) {
        errors.push({
          path: currentPath,
          message: `Value must be ${JSON.stringify(schemaNode.const)}`,
          keyword: 'const',
          params: { expected: schemaNode.const, actual: value }
        })
      }
    }

    // String validations
    if (typeof value === 'string') {
      if (typeof schemaNode.minLength === 'number' && value.length < schemaNode.minLength) {
        errors.push({
          path: currentPath,
          message: `String must be at least ${schemaNode.minLength} characters`,
          keyword: 'minLength',
          params: { limit: schemaNode.minLength, actual: value.length }
        })
      }

      if (typeof schemaNode.maxLength === 'number' && value.length > schemaNode.maxLength) {
        errors.push({
          path: currentPath,
          message: `String must be at most ${schemaNode.maxLength} characters`,
          keyword: 'maxLength',
          params: { limit: schemaNode.maxLength, actual: value.length }
        })
      }

      if (schemaNode.pattern && typeof schemaNode.pattern === 'string') {
        const regex = new RegExp(schemaNode.pattern)
        if (!regex.test(value)) {
          errors.push({
            path: currentPath,
            message: `String must match pattern: ${schemaNode.pattern}`,
            keyword: 'pattern',
            params: { pattern: schemaNode.pattern }
          })
        }
      }

      // Format validation
      if (schemaNode.format) {
        const formatError = validateFormat(value, schemaNode.format as string)
        if (formatError) {
          errors.push({
            path: currentPath,
            message: formatError,
            keyword: 'format',
            params: { format: schemaNode.format }
          })
        }
      }
    }

    // Number validations
    if (typeof value === 'number') {
      if (typeof schemaNode.minimum === 'number' && value < schemaNode.minimum) {
        errors.push({
          path: currentPath,
          message: `Value must be >= ${schemaNode.minimum}`,
          keyword: 'minimum',
          params: { limit: schemaNode.minimum, actual: value }
        })
      }

      if (typeof schemaNode.maximum === 'number' && value > schemaNode.maximum) {
        errors.push({
          path: currentPath,
          message: `Value must be <= ${schemaNode.maximum}`,
          keyword: 'maximum',
          params: { limit: schemaNode.maximum, actual: value }
        })
      }

      if (typeof schemaNode.exclusiveMinimum === 'number' && value <= schemaNode.exclusiveMinimum) {
        errors.push({
          path: currentPath,
          message: `Value must be > ${schemaNode.exclusiveMinimum}`,
          keyword: 'exclusiveMinimum',
          params: { limit: schemaNode.exclusiveMinimum, actual: value }
        })
      }

      if (typeof schemaNode.exclusiveMaximum === 'number' && value >= schemaNode.exclusiveMaximum) {
        errors.push({
          path: currentPath,
          message: `Value must be < ${schemaNode.exclusiveMaximum}`,
          keyword: 'exclusiveMaximum',
          params: { limit: schemaNode.exclusiveMaximum, actual: value }
        })
      }

      if (typeof schemaNode.multipleOf === 'number' && value % schemaNode.multipleOf !== 0) {
        errors.push({
          path: currentPath,
          message: `Value must be a multiple of ${schemaNode.multipleOf}`,
          keyword: 'multipleOf',
          params: { multipleOf: schemaNode.multipleOf, actual: value }
        })
      }
    }

    // Array validations
    if (Array.isArray(value)) {
      if (typeof schemaNode.minItems === 'number' && value.length < schemaNode.minItems) {
        errors.push({
          path: currentPath,
          message: `Array must have at least ${schemaNode.minItems} items`,
          keyword: 'minItems',
          params: { limit: schemaNode.minItems, actual: value.length }
        })
      }

      if (typeof schemaNode.maxItems === 'number' && value.length > schemaNode.maxItems) {
        errors.push({
          path: currentPath,
          message: `Array must have at most ${schemaNode.maxItems} items`,
          keyword: 'maxItems',
          params: { limit: schemaNode.maxItems, actual: value.length }
        })
      }

      if (schemaNode.uniqueItems === true) {
        const seen = new Set()
        for (let i = 0; i < value.length; i++) {
          const key = JSON.stringify(value[i])
          if (seen.has(key)) {
            errors.push({
              path: `${currentPath}[${i}]`,
              message: 'Array items must be unique',
              keyword: 'uniqueItems'
            })
            break
          }
          seen.add(key)
        }
      }

      // Validate items
      if (schemaNode.items && typeof schemaNode.items === 'object') {
        value.forEach((item, index) => {
          validate(item, schemaNode.items as Record<string, unknown>, `${currentPath}[${index}]`)
        })
      }
    }

    // Object validations
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const obj = value as Record<string, unknown>
      const keys = Object.keys(obj)

      // Required properties
      if (Array.isArray(schemaNode.required)) {
        for (const reqKey of schemaNode.required) {
          if (!(reqKey in obj)) {
            errors.push({
              path: `${currentPath}.${reqKey}`,
              message: `Missing required property: ${reqKey}`,
              keyword: 'required',
              params: { missingProperty: reqKey }
            })
          }
        }
      }

      // Property count limits
      if (typeof schemaNode.minProperties === 'number' && keys.length < schemaNode.minProperties) {
        errors.push({
          path: currentPath,
          message: `Object must have at least ${schemaNode.minProperties} properties`,
          keyword: 'minProperties',
          params: { limit: schemaNode.minProperties, actual: keys.length }
        })
      }

      if (typeof schemaNode.maxProperties === 'number' && keys.length > schemaNode.maxProperties) {
        errors.push({
          path: currentPath,
          message: `Object must have at most ${schemaNode.maxProperties} properties`,
          keyword: 'maxProperties',
          params: { limit: schemaNode.maxProperties, actual: keys.length }
        })
      }

      // Validate properties
      const properties = schemaNode.properties as Record<string, Record<string, unknown>> | undefined

      if (properties) {
        for (const [key, propSchema] of Object.entries(properties)) {
          if (key in obj) {
            validate(obj[key], propSchema, `${currentPath}.${key}`)
          }
        }
      }

      // Additional properties
      if (schemaNode.additionalProperties === false && properties) {
        const allowedKeys = new Set(Object.keys(properties))
        for (const key of keys) {
          if (!allowedKeys.has(key)) {
            errors.push({
              path: `${currentPath}.${key}`,
              message: `Additional property not allowed: ${key}`,
              keyword: 'additionalProperties',
              params: { additionalProperty: key }
            })
          }
        }
      }
    }
  }

  validate(data, schema, path)

  return {
    valid: errors.length === 0,
    errors
  }
}

function getJsonType(value: unknown): string {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'array'
  if (typeof value === 'number') {
    return Number.isInteger(value) ? 'integer' : 'number'
  }
  return typeof value
}

function validateFormat(value: string, format: string): string | null {
  switch (format) {
    case 'email':
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return 'Invalid email format'
      }
      break

    case 'uri':
    case 'url':
      try {
        new URL(value)
      } catch {
        return 'Invalid URL format'
      }
      break

    case 'uuid':
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
        return 'Invalid UUID format'
      }
      break

    case 'date':
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || isNaN(Date.parse(value))) {
        return 'Invalid date format (expected YYYY-MM-DD)'
      }
      break

    case 'date-time':
      if (isNaN(Date.parse(value))) {
        return 'Invalid date-time format'
      }
      break

    case 'time':
      if (!/^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
        return 'Invalid time format (expected HH:MM or HH:MM:SS)'
      }
      break

    case 'ipv4':
      if (!/^(\d{1,3}\.){3}\d{1,3}$/.test(value)) {
        return 'Invalid IPv4 format'
      }
      break

    case 'ipv6':
      if (!/^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/.test(value)) {
        return 'Invalid IPv6 format'
      }
      break
  }

  return null
}

/**
 * Parse a JSON Schema from string
 */
export function parseJsonSchema(schemaString: string): Record<string, unknown> {
  try {
    const schema = JSON.parse(schemaString)
    if (typeof schema !== 'object' || schema === null) {
      throw new Error('Schema must be an object')
    }
    return schema
  } catch (err) {
    throw new Error(`Invalid JSON Schema: ${err instanceof Error ? err.message : 'Unknown error'}`)
  }
}

/**
 * Get paths with errors for visual highlighting
 */
export function getErrorPaths(errors: ValidationError[]): Set<string> {
  return new Set(errors.map(e => e.path))
}

/**
 * Check if a path has an error
 */
export function hasError(path: string, errorPaths: Set<string>): boolean {
  // Check exact match
  if (errorPaths.has(path)) return true

  // Check if any error path is a child of this path
  for (const errorPath of errorPaths) {
    if (errorPath.startsWith(path + '.') || errorPath.startsWith(path + '[')) {
      return true
    }
  }

  return false
}
